import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';

import { provideFlagIcon } from './IconProvider';
import './LanguageSelector.css';
import { FALLBACK_LANGUAGE, formatLanguageLabel, Language, getAvailableLanguages } from './translateFunctions';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { LANGUAGE_LOCAL_STORAGE_KEY } from '@/utilities/language';

function getLanguageByCode(languages: Language[], code: string | undefined) {
  return languages.find((language) => language.code === code);
}

function getFallbackLanguage(languages: Language[]) {
  return getLanguageByCode(languages, 'en') ?? languages[0] ?? FALLBACK_LANGUAGE;
}

function LanguageOption({ language, isMobile }: { language: Language; isMobile: boolean }) {
  return (
    <Box
      className={`language-selector-option-content ${
        isMobile ? 'language-selector-option-content-compact' : 'language-selector-option-content-full'
      }`}
    >
      <Box className="language-selector-list-icon-frame">
        <img className="language-selector-list-icon" src={provideFlagIcon(language.code)} alt="" />
      </Box>
      <Typography>{formatLanguageLabel(language, isMobile)}</Typography>
    </Box>
  );
}

const LanguageSelector = ({ compact = false }: { compact?: boolean }) => {
  const { i18n } = useTranslation();
  const availableLanguages: Language[] = useMemo(() => getAvailableLanguages(), []);
  const fallbackLanguage = getFallbackLanguage(availableLanguages);
  const [optimisticCode, setOptimisticCode] = useState<string | undefined>();
  const isMobile = compact || isPhoneOrHighScreen();

  async function languageChange(event: SelectChangeEvent<string>) {
    const language = getLanguageByCode(availableLanguages, event.target.value);
    if (language === undefined) return;

    setOptimisticCode(language.code);
    await i18n.changeLanguage(language.code);
    localStorage.setItem(LANGUAGE_LOCAL_STORAGE_KEY, language.code);
    setOptimisticCode(undefined);
  }

  const selected = getLanguageByCode(availableLanguages, optimisticCode ?? i18n.language) ?? fallbackLanguage;

  return (
    <FormControl
      className={`language-selector-control ${
        isMobile ? 'language-selector-control-compact' : 'language-selector-control-full'
      }`}
      data-qa-element-id="language-selector"
      size="small"
    >
      <Select<string>
        className={`language-selector-select ${
          isMobile ? 'language-selector-select-compact' : 'language-selector-select-full'
        }`}
        classes={{
          select: 'language-selector-value',
          icon: 'language-selector-icon',
        }}
        value={selected.code}
        onChange={languageChange}
        displayEmpty
        inputProps={{
          'aria-label': 'Language selector',
          'data-qa-element-id': 'language-selector-input',
        }}
        MenuProps={{
          classes: { paper: 'language-selector-menu' },
          MenuListProps: {
            className: 'language-selector-menu-list',
            'aria-label': 'Supported languages',
          },
        }}
        renderValue={(value) => {
          const language = getLanguageByCode(availableLanguages, value) ?? selected;
          return <LanguageOption language={language} isMobile={isMobile} />;
        }}
      >
        {availableLanguages.map((language) => (
          <MenuItem
            key={language.code}
            value={language.code}
            className="language-selector-menu-item"
            data-qa-element-id={`language-selector-language-${language.code}`}
          >
            <LanguageOption language={language} isMobile={isMobile} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LanguageSelector;
